import type { PurchasedProductItemDto } from '@/types/api/product';

type PurchasedRow = PurchasedProductItemDto & {
    productPrice?: Record<string, unknown>;
};

/** GET /product/organization/purchased 행: 중첩 productPrice 우선, 그다음 루트 productPriceId */
function nestedProductPriceId(nested: unknown): number {
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return 0;
    const o = nested as Record<string, unknown>;
    const raw = o.productPriceId ?? o.id;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function rowProductPriceId(row: PurchasedRow): number {
    const fromNested = nestedProductPriceId(row.productPrice);
    if (fromNested > 0) return fromNested;
    if (typeof row.productPriceId === 'number' && row.productPriceId > 0) return row.productPriceId;
    return 0;
}

function preferUnlimitedPurchasedRow(matches: PurchasedProductItemDto[]): PurchasedProductItemDto {
    return matches.find((r) => r.purchaseType === 'PER_DEVICE' || r.purchaseType === 'PER_LICENSE' || r.purchaseType === 'PER_ITEM')
        ?? matches[0];
}

/**
 * GET /product/organization/purchased 행에서 클래스 생성·수정용 productPriceId 결정.
 * 동일 productId가 여러 주문에 있으면 preferredOrderId 행을 우선합니다.
 */
export function resolvePurchasedProductPriceId(
    rows: PurchasedProductItemDto[] | undefined,
    productId: number,
    preferredOrderId?: number | null
): number {
    if (!rows?.length || productId <= 0) return 0;
    const matches = rows.filter((r) => r && r.productId === productId);
    if (matches.length === 0) return 0;
    if (preferredOrderId != null && preferredOrderId > 0) {
        const byOrder = matches.find((r) => r.orderId === preferredOrderId);
        if (byOrder) {
            const id = rowProductPriceId(byOrder as PurchasedRow);
            if (id > 0) return id;
        }
    }
    const preferred = preferUnlimitedPurchasedRow(matches);
    const ordered = [preferred, ...matches.filter((m) => m !== preferred)];
    for (const m of ordered) {
        const id = rowProductPriceId(m as PurchasedRow);
        if (id > 0) return id;
    }
    return 0;
}

export function findPurchasedRowForProduct(
    rows: PurchasedProductItemDto[] | undefined,
    productId: number,
    preferredOrderId?: number | null
): PurchasedProductItemDto | undefined {
    if (!rows?.length || productId <= 0) return undefined;
    const matches = rows.filter((r) => r && r.productId === productId);
    if (matches.length === 0) return undefined;
    if (preferredOrderId != null && preferredOrderId > 0) {
        const byOrder = matches.find((r) => r.orderId === preferredOrderId);
        if (byOrder) return byOrder;
    }
    return preferUnlimitedPurchasedRow(matches);
}
